/**
 * v-can — one gate for the Add / Edit / Delete controls on every page.
 *
 * THE PROBLEM THIS EXISTS TO STOP.
 *
 * The permissions matrix (85 features x View/Add/Edit/Delete) is enforced in
 * three places that never agreed with each other:
 *
 *   - the menu          hides a nav item on `<slug>__View`
 *   - the router guard  bounces the URL on `<slug>__View`
 *   - the API           `permission:` middleware, real 403s
 *
 * Nothing gated the BUTTONS. A page the user is allowed to open renders its
 * full toolbar, so a View-only role sees "Add Supplier/Trade", clicks it, and
 * either gets a raw 403 (for the handful of features in
 * PERMISSION_ENFORCED_FEATURES) or silently succeeds (for every feature still
 * in shadow mode). Same matrix, same role, two opposite outcomes depending on
 * a server env var the user cannot see — which is what "permissions feel
 * random" actually means.
 *
 * Eighteen files had grown their own hand-rolled version of this check and
 * thirty-two had none. This is the one implementation.
 *
 * USAGE
 *
 *   v-can="'suppliers__Add'"        hide unless the role grants suppliers__Add
 *   v-can="'suppliers'"             action defaults to View
 *   v-can:edit="'suppliers'"        action from the directive argument
 *   v-can="['suppliers__Add', 'suppliers__Edit']"   OR — any one grants
 *   v-can.disable="'suppliers__Add'"  grey the control out instead of hiding it
 *   v-can.readonly="['jobs__Add', 'jobs__Edit']"   make a whole FORM read-only
 *
 * `.readonly` is for the fields rather than the controls, and it goes on the
 * CONTAINER -- a dialog, a card, a <form> -- not on each input. Hiding the Save
 * button stops the write but leaves a form that still looks editable: a
 * view-only user types into it, finds no way to save, and reasonably reports
 * that saving is broken. This makes the whole form state what it is.
 *
 * It is NOT `.disable`. Disabling greys a field to half opacity and drops it
 * from the tab order, which is wrong for a form whose entire purpose is still
 * to be READ. Text inputs get the native `readonly` attribute: full contrast,
 * still focusable, still selectable and copyable, just not editable.
 *
 * Buttons, links and tabs inside the container stay live -- Cancel must still
 * close, and tabs must still switch -- so only form controls are neutralised.
 * The controls `readonly` does not cover (checkboxes, radios, selects, file
 * inputs, Vuetify's select/autocomplete fields and the date pickers) are
 * blocked by one capture-phase listener on the container instead.
 *
 * Fields that appear LATER are covered too: dialogs mount their body lazily and
 * rows are added after the fact, so a MutationObserver re-runs the sweep on
 * anything added while the container is read-only.
 *
 * The permission KEY must be a catalogue slug from bsapi
 * config/permission_catalogue.php. Route names are the same slugs (the router
 * guard and the nav build `<route name>__View`), so `suppliers`, `customers`,
 * `jobs` etc. are all valid here.
 *
 * NOT A SECURITY BOUNDARY. This hides UI the user may not use; the server is
 * what refuses the request. Hiding a button whose endpoint is still in shadow
 * mode makes the UI honest, it does not make the endpoint safe — that needs the
 * feature added to PERMISSION_ENFORCED_FEATURES.
 *
 * FAILS CLOSED, deliberately, matching utils/permissions.hasPermission(): a key
 * the role does not mention is denied. A control that flashes on and then
 * 403s is worse than one that never appears.
 *
 * WHY display:none AND NOT REMOVING THE NODE. Vue patches this element from its
 * vnode on every re-render. Detaching it from the DOM here desynchronises the
 * vdom and the next patch throws or resurrects the element. Toggling inline
 * display is patch-safe and re-evaluated in `updated`, so the gate follows a
 * role change without a reload.
 */

import type { Directive, DirectiveBinding } from 'vue';
import { store } from '@/storeVuex';
import { isFullAdmin } from '@/utils/fullAdmin';
import { hasPermission } from '@/utils/permissions';

/** Remembers each element's own inline display so hiding is reversible. */
const PRIOR_DISPLAY = new WeakMap<HTMLElement, string>();
/** The capture-phase blocker installed by `.disable`, so it can be removed. */
const BLOCKERS = new WeakMap<HTMLElement, EventListener>();

const CLICKISH = ['click', 'mousedown', 'mouseup', 'keydown', 'touchstart'];

/**
 * "suppliers__Add" -> ['suppliers', 'Add'];  "suppliers" -> ['suppliers', arg].
 *
 * The argument form (v-can:edit) is lowercase in the template but the stored
 * suffix is capitalised (role_settings holds "suppliers__Edit"), so normalise
 * rather than trusting the author's casing.
 */
function splitKey(raw: string, arg?: string): [string, string] {
    const value = String(raw ?? '').trim();
    if (!value) return ['', 'View'];

    if (value.includes('__')) {
        const [slug, action] = value.split('__');
        return [slug, capitalise(action)];
    }

    return [value, capitalise(arg || 'View')];
}

function capitalise(action: string): string {
    const a = String(action ?? '').trim();
    if (!a) return 'View';
    // "view_all" -> "View_All": each underscore-separated part is capitalised,
    // because the Team Tracker keys are stored that way.
    return a
        .split('_')
        .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : p))
        .join('_');
}

/** Does the signed-in user hold any one of the bound keys? */
function granted(binding: DirectiveBinding): boolean {
    const user = (store as any)?.getters?.isLoggedIn?.user;

    // Full admins bypass the matrix entirely — same rule the router guard and
    // the server (User::isInAppAdmin) apply.
    if (isFullAdmin(user)) return true;

    const raw = binding.value;
    const keys = Array.isArray(raw) ? raw : [raw];

    // No key given is an authoring mistake, not a grant. Say so loudly in dev
    // and deny, rather than quietly showing the control to everyone.
    const usable = keys.filter((k) => String(k ?? '').trim() !== '');
    if (usable.length === 0) {
        if (import.meta.env.DEV) {
            console.warn('[v-can] no permission key given; the element will be hidden');
        }
        return false;
    }

    return usable.some((k) => {
        const [slug, action] = splitKey(String(k), binding.arg);
        return slug !== '' && hasPermission(user, slug, action);
    });
}

function hide(el: HTMLElement): void {
    if (!PRIOR_DISPLAY.has(el)) {
        PRIOR_DISPLAY.set(el, el.style.display);
    }
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
}

function show(el: HTMLElement): void {
    if (PRIOR_DISPLAY.has(el)) {
        el.style.display = PRIOR_DISPLAY.get(el) as string;
        PRIOR_DISPLAY.delete(el);
    }
    el.removeAttribute('aria-hidden');
}

/**
 * `.disable` keeps the control visible but inert.
 *
 * Three things are needed and none is sufficient alone: the `disabled`
 * attribute (native buttons and inputs), pointer-events (the <div> icon
 * buttons the tables use, which ignore `disabled`), and a capture-phase
 * listener — because a Vue @click bound on the element itself still fires even
 * when pointer-events is none if the event is dispatched programmatically.
 */
function disable(el: HTMLElement): void {
    el.setAttribute('disabled', 'disabled');
    el.setAttribute('aria-disabled', 'true');
    el.classList.add('v-can-disabled');
    el.style.pointerEvents = 'none';
    el.style.opacity = '0.5';
    el.style.cursor = 'not-allowed';

    if (BLOCKERS.has(el)) return;
    const blocker: EventListener = (event) => {
        event.stopImmediatePropagation();
        event.preventDefault();
    };
    BLOCKERS.set(el, blocker);
    CLICKISH.forEach((type) => el.addEventListener(type, blocker, true));
}

function enable(el: HTMLElement): void {
    el.removeAttribute('disabled');
    el.removeAttribute('aria-disabled');
    el.classList.remove('v-can-disabled');
    el.style.pointerEvents = '';
    el.style.opacity = '';
    el.style.cursor = '';

    const blocker = BLOCKERS.get(el);
    if (blocker) {
        CLICKISH.forEach((type) => el.removeEventListener(type, blocker, true));
        BLOCKERS.delete(el);
    }
}

/* ------------------------------------------------------------------------- *
 * .readonly — a whole form, read but not written
 * ------------------------------------------------------------------------- */

/** Observers and blockers installed by `.readonly`, so they can be removed. */
const OBSERVERS = new WeakMap<HTMLElement, MutationObserver>();
const RO_BLOCKERS = new WeakMap<HTMLElement, EventListener>();

/** Elements this directive set `readonly` on, so it only clears its own. */
const RO_OWNED = new WeakMap<HTMLElement, Set<Element>>();

/**
 * Controls the native `readonly` attribute does nothing for.
 *
 * `readonly` is specified for text-entry controls only. A checkbox, radio,
 * file, range or <select> ignores it completely, and Vuetify's select,
 * autocomplete and date pickers open their menu from the FIELD wrapper rather
 * than the inner input — which is already `readonly` by design. All of these
 * need the event blocker instead.
 */
const OBSERVE_OPTS: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['readonly']
};

const UNPROTECTED = [
    'input[type="checkbox"]',
    'input[type="radio"]',
    'input[type="file"]',
    'input[type="range"]',
    'input[type="color"]',
    'select',
    '.v-selection-control',
    '.v-select .v-field',
    '.v-autocomplete .v-field',
    '.v-combobox .v-field',
    '.v-slider',
    '.dp__main'
].join(',');

/** Things that must keep working inside a read-only form. */
const STAYS_LIVE = 'button,a,[role="tab"],[role="button"],.v-btn,.v-tab,.v-expansion-panel-title';

/** Text-entry controls, where the native attribute is the right answer. */
function textControls(root: HTMLElement): Element[] {
    return Array.from(root.querySelectorAll('input,textarea')).filter((el) => {
        const type = (el.getAttribute('type') || 'text').toLowerCase();
        return el.tagName === 'TEXTAREA' || !['checkbox', 'radio', 'file', 'range', 'color', 'button', 'submit'].includes(type);
    });
}

function markReadonly(root: HTMLElement): void {
    const owned = RO_OWNED.get(root) ?? new Set<Element>();

    for (const el of textControls(root)) {
        // Never claim a field the page itself marked readonly, or restoring
        // would hand edit access to something that was never editable.
        if (el.hasAttribute('readonly')) {
            if (!owned.has(el)) continue;
        } else {
            el.setAttribute('readonly', 'readonly');
            owned.add(el);
        }
        el.setAttribute('aria-readonly', 'true');
    }

    RO_OWNED.set(root, owned);
    root.classList.add('v-can-readonly');
}

function clearReadonly(root: HTMLElement): void {
    const owned = RO_OWNED.get(root);
    if (owned) {
        for (const el of owned) {
            el.removeAttribute('readonly');
            el.removeAttribute('aria-readonly');
        }
        RO_OWNED.delete(root);
    }
    root.classList.remove('v-can-readonly');
}

/**
 * Swallow interactions aimed at a control `readonly` cannot protect.
 *
 * Capture phase on the CONTAINER, because Vuetify binds its own handlers on the
 * field wrapper and a bubbling listener would run after the menu had opened.
 */
function readonlyBlocker(): EventListener {
    return (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (!target?.closest) return;

        // Cancel still closes, tabs still switch, panels still expand.
        if (target.closest(STAYS_LIVE)) return;

        // A readonly text field keeps focus, selection and copy.
        if (!target.closest(UNPROTECTED)) return;

        event.stopImmediatePropagation();
        event.preventDefault();
    };
}

function enterReadonly(el: HTMLElement): void {
    markReadonly(el);

    if (!RO_BLOCKERS.has(el)) {
        const blocker = readonlyBlocker();
        RO_BLOCKERS.set(el, blocker);
        CLICKISH.forEach((type) => el.addEventListener(type, blocker, true));
    }

    // Two ways a field escapes the sweep, both of which happen in practice:
    //
    //  - it mounts LATER. Dialog bodies and tab panes render lazily and lists
    //    grow, so a field that did not exist at the first pass comes up
    //    editable.
    //  - it loses the attribute. `readonly` is set on the DOM node, not through
    //    the vnode, so a component that re-renders with its own `:readonly`
    //    binding can patch ours away underneath us.
    //
    // The observer therefore watches childList AND the readonly attribute, and
    // disconnects around its own writes — re-marking inside the callback would
    // otherwise queue another record and spin.
    if (!OBSERVERS.has(el) && typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => {
            observer.disconnect();
            markReadonly(el);
            observer.observe(el, OBSERVE_OPTS);
        });
        observer.observe(el, OBSERVE_OPTS);
        OBSERVERS.set(el, observer);
    }
}

function exitReadonly(el: HTMLElement): void {
    OBSERVERS.get(el)?.disconnect();
    OBSERVERS.delete(el);

    const blocker = RO_BLOCKERS.get(el);
    if (blocker) {
        CLICKISH.forEach((type) => el.removeEventListener(type, blocker, true));
        RO_BLOCKERS.delete(el);
    }

    clearReadonly(el);
}

function apply(el: HTMLElement, binding: DirectiveBinding): void {
    const allowed = granted(binding);

    if (binding.modifiers.readonly) {
        // A read-only form is still shown and its buttons are still governed by
        // their own v-can, so neither hide() nor disable() applies here.
        show(el);
        enable(el);
        allowed ? exitReadonly(el) : enterReadonly(el);
        return;
    }

    if (binding.modifiers.disable) {
        // Never leave an element both hidden and disabled if the modifier was
        // added after a first pass hid it.
        show(el);
        allowed ? enable(el) : disable(el);
        return;
    }

    enable(el);
    allowed ? show(el) : hide(el);
}

export const can: Directive<HTMLElement, string | string[]> = {
    mounted: apply,
    updated: apply,
    unmounted(el) {
        exitReadonly(el);
        PRIOR_DISPLAY.delete(el);
        const blocker = BLOCKERS.get(el);
        if (blocker) {
            CLICKISH.forEach((type) => el.removeEventListener(type, blocker, true));
            BLOCKERS.delete(el);
        }
    }
};

export default can;
